import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Grid,
  GridItem,
  useColorModeValue,
  useToast,
  Icon,
  Divider,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  FormHelperText,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Stack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Spinner,
  Collapse,
  useDisclosure as useCollapse,
} from '@chakra-ui/react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  FiDollarSign,
  FiLock,
  FiUnlock,
  FiArrowRight,
  FiTrendingUp,
  FiPieChart,
  FiAlertCircle,
  FiRefreshCw,
  FiExternalLink,
  FiDownload,
  FiEye,
  FiArrowLeft,
  FiUsers,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiMaximize2,
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiChevronDown,
  FiHash,
  FiCalendar,
  FiDollarSign as FiDollar,
  FiZap,
} from 'react-icons/fi';
import { useAccount, useContractRead, useContractWrite, useWaitForTransaction, useBalance } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConnectButton from '@/components/common/ConnectButton';
import PageHeader from '@/components/common/PageHeader';
import { formatDOR, formatUSD, formatAddress } from '@/utils/formatters';

// 模拟DOR余额数据
const mockDORData = {
  circulating: 150.00,
  locked: 200.00,
  total: 350.00,
  circulatingValue: 148.50, // 基于当前价格0.99
  lockedValue: 196.50,
  totalValue: 345.00,
  unlockProgress: 20, // 已赎回20%
  conversionRate: 0.95, // 95%转换率
  minConversion: 10,
  maxConversion: 1000,
};

// 模拟交易历史数据
const mockTransactionHistory = [
  {
    id: 1,
    type: 'convert',
    amount: 50,
    usdtImpact: 0,
    timestamp: '2025-07-29 10:30',
    txHash: '0xabc123def4567890123456789012345678901234567890abcdef',
    status: 'completed',
    description: '转换为锁定DOR',
    gasUsed: 0.002,
    blockNumber: 12345678,
    confirmations: 12,
  },
  {
    id: 2,
    type: 'destroy',
    amount: -30,
    usdtImpact: 30,
    timestamp: '2025-07-28 15:45',
    txHash: '0xdef456abc7890123456789012345678901234567890abcdef123',
    status: 'completed',
    description: '邀请销毁，获返USDT',
    gasUsed: 0.0015,
    blockNumber: 12345670,
    confirmations: 15,
  },
  {
    id: 3,
    type: 'sell',
    amount: -20,
    usdtImpact: 19.8,
    timestamp: '2025-07-27 09:15',
    txHash: '0x111222333444555666777888999000111222333444555666777',
    status: 'completed',
    description: '外盘卖出流通DOR',
    gasUsed: 0.003,
    blockNumber: 12345665,
    confirmations: 20,
  },
  {
    id: 4,
    type: 'convert',
    amount: 25,
    usdtImpact: 0,
    timestamp: '2025-07-26 14:20',
    txHash: '0x333444555666777888999000111222333444555666777888999',
    status: 'pending',
    description: '转换为锁定DOR',
    gasUsed: 0.002,
    blockNumber: 12345660,
    confirmations: 0,
  },
  {
    id: 5,
    type: 'recharge',
    amount: 100,
    usdtImpact: -100,
    timestamp: '2025-07-25 16:30',
    txHash: '0x555666777888999000111222333444555666777888999000111',
    status: 'completed',
    description: '充值获得DOR',
    gasUsed: 0.004,
    blockNumber: 12345655,
    confirmations: 25,
  },
  {
    id: 6,
    type: 'destroy',
    amount: -15,
    usdtImpact: 15,
    timestamp: '2025-07-24 11:20',
    txHash: '0x777888999000111222333444555666777888999000111222333',
    status: 'completed',
    description: '邀请销毁，获返USDT',
    gasUsed: 0.0018,
    blockNumber: 12345650,
    confirmations: 30,
  },
  {
    id: 7,
    type: 'convert',
    amount: 40,
    usdtImpact: 0,
    timestamp: '2025-07-23 13:45',
    txHash: '0x999000111222333444555666777888999000111222333444555',
    status: 'completed',
    description: '转换为锁定DOR',
    gasUsed: 0.0022,
    blockNumber: 12345645,
    confirmations: 35,
  },
  {
    id: 8,
    type: 'sell',
    amount: -10,
    usdtImpact: 9.9,
    timestamp: '2025-07-22 08:15',
    txHash: '0x000111222333444555666777888999000111222333444555666',
    status: 'completed',
    description: '外盘卖出流通DOR',
    gasUsed: 0.0025,
    blockNumber: 12345640,
    confirmations: 40,
  },
];

// 饼图数据
const pieChartData = [
  { name: '流通DOR', value: mockDORData.circulating, color: '#38A169' },
  { name: '锁定DOR', value: mockDORData.locked, color: '#ED8936' },
];

// 历史图表数据
const historyChartData = [
  { date: '2025-07-25', circulating: 100, locked: 150, total: 250 },
  { date: '2025-07-26', circulating: 75, locked: 175, total: 250 },
  { date: '2025-07-27', circulating: 55, locked: 175, total: 230 },
  { date: '2025-07-28', circulating: 55, locked: 145, total: 200 },
  { date: '2025-07-29', circulating: 5, locked: 195, total: 200 },
];

const DORManagement: React.FC = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('management');
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 状态管理
  const [activeTab, setActiveTab] = useState(0);
  const [conversionAmount, setConversionAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // 计算预览数据
  const previewData = useMemo(() => {
    if (!conversionAmount || parseFloat(conversionAmount) === 0) return null;
    
    const amount = parseFloat(conversionAmount);
    const convertedAmount = amount; // 1:1转换，无手续费
    
    return {
      inputAmount: amount,
      convertedAmount,
      gasEstimate: 0.005, // 模拟gas估算
    };
  }, [conversionAmount]);

  // 处理金额输入
  const handleAmountChange = (value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setConversionAmount(value);
    }
  };

  // 设置最大金额
  const setMaxAmount = () => {
    setConversionAmount(mockDORData.circulating.toString());
  };

  // 转换操作
  const handleConversion = async () => {
    if (!conversionAmount || parseFloat(conversionAmount) < mockDORData.minConversion) {
      toast({
        title: t('inputError'),
        description: t('minAmountError', { amount: mockDORData.minConversion }),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (parseFloat(conversionAmount) > mockDORData.circulating) {
      toast({
        title: t('insufficientBalance'),
        description: t('insufficientBalanceDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      // 模拟合约调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: t('convertSuccess'),
        description: t('convertSuccessDesc', { amount: conversionAmount }),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      setConversionAmount('');
      onClose();
    } catch (error) {
      toast({
        title: t('convertFailed'),
        description: t('convertFailedDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 刷新余额
  const handleRefreshBalance = async () => {
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: t('refreshSuccess'),
        description: t('refreshSuccessDesc'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('refreshFailed'),
        description: t('refreshFailedDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // 外盘卖出
  const handleExternalSell = () => {
    const uniswapUrl = `https://app.uniswap.org/#/swap?outputCurrency=0x1234567890123456789012345678901234567890&exactAmount=${mockDORData.circulating}`;
    window.open(uniswapUrl, '_blank');
    
    toast({
      title: t('redirectingToExternal'),
      description: t('redirectingToExternalDesc'),
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  // 导出历史
  const exportHistory = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "类型,金额,USDT影响,时间,状态,描述\n"
      + mockTransactionHistory.map(item => 
          `${item.type},${item.amount},${item.usdtImpact},${item.timestamp},${item.status},${item.description}`
        ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "dor_transaction_history.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: t('exportSuccess'),
      description: t('exportSuccessDesc'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // 过滤、搜索和排序交易历史
  const filteredHistory = useMemo(() => {
    let filtered = mockTransactionHistory;

    // 按类型过滤
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }

    // 按搜索词过滤
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.txHash.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.amount.toString().includes(searchTerm)
      );
    }

    // 排序
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'amount':
          aValue = Math.abs(a.amount);
          bValue = Math.abs(b.amount);
          break;
        case 'timestamp':
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        case 'usdtImpact':
          aValue = Math.abs(a.usdtImpact);
          bValue = Math.abs(b.usdtImpact);
          break;
        default:
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
      }

      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return filtered;
  }, [filterType, searchTerm, sortBy, sortOrder]);

  // 分页数据
  const paginatedHistory = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredHistory.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredHistory, currentPage, itemsPerPage]);

  // 总页数
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  // 统计摘要数据
  const statsData = useMemo(() => {
    const stats = {
      totalConverted: 0,
      totalDestroyed: 0,
      totalSold: 0,
      totalRecharged: 0,
      totalUsdtImpact: 0,
    };

    mockTransactionHistory.forEach(item => {
      switch (item.type) {
        case 'convert':
          stats.totalConverted += Math.abs(item.amount);
          break;
        case 'destroy':
          stats.totalDestroyed += Math.abs(item.amount);
          stats.totalUsdtImpact += item.usdtImpact;
          break;
        case 'sell':
          stats.totalSold += Math.abs(item.amount);
          stats.totalUsdtImpact += item.usdtImpact;
          break;
        case 'recharge':
          stats.totalRecharged += item.amount;
          break;
      }
    });

    return stats;
  }, []);

  // 渲染内容
  const renderContent = () => {
    // 如果未连接钱包，显示连接提示
    if (!isConnected) {
      return (
        <Box>
          <VStack spacing={6} align="stretch">
            {/* 顶部区域 */}
            <PageHeader title={t('title')} />
            
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody textAlign="center" py={12}>
                <Box mb={4}><FiPieChart size={64} color="var(--chakra-colors-blue-500)" /></Box>
                <Heading size="md" mb={2}>
                  {t('connectWalletPrompt')}
                </Heading>
                <Text color="gray.500" mb={6}>
                  {t('connectWalletDescription')}
                </Text>
                <ConnectButton />
              </CardBody>
            </Card>
          </VStack>
        </Box>
      );
    }

    return (
      <Box>
        <VStack spacing={6} align="stretch">
          {/* 顶部区域 */}
          <PageHeader title={t('title')} />

          {/* 总览统计 */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.500">{t('totalDOR')}</Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {formatDOR(mockDORData.total)}
                  </Text>
                </VStack>
                <VStack align="end" spacing={1}>
                  <Text fontSize="sm" color="gray.500">{t('totalValue')}</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.500">
                    {formatUSD(mockDORData.totalValue)}
                  </Text>
                </VStack>
              </HStack>
            </CardBody>
          </Card>

          {/* 主体内容区 */}
          <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
          <TabList>
            <Tab>{t('overview')}</Tab>
            <Tab>{t('operations')}</Tab>
            <Tab>{t('history')}</Tab>
          </TabList>

          <TabPanels>
            {/* 余额概览模块 */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
                  {/* 统计卡片组 */}
                  <VStack spacing={4}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                      <Card bg={cardBg} border="1px" borderColor={borderColor}>
                        <CardBody>
                          <VStack align="start" spacing={2}>
                            <HStack justify="space-between" w="full">
                              <Text fontSize="sm" color="gray.500">{t('circulatingDOR')}</Text>
                              <Tooltip label={t('circulatingTooltip')}>
                                <Box>
                                  <FiInfo color="gray.400" />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <Text fontSize="xl" fontWeight="bold" color="green.500">
                              {formatDOR(mockDORData.circulating)}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {t('estimatedValue')}: {formatUSD(mockDORData.circulatingValue)}
                            </Text>
                          </VStack>
                        </CardBody>
                      </Card>

                      <Card bg={cardBg} border="1px" borderColor={borderColor}>
                        <CardBody>
                          <VStack align="start" spacing={2}>
                            <HStack justify="space-between" w="full">
                              <Text fontSize="sm" color="gray.500">{t('lockedDOR')}</Text>
                              <Tooltip label={t('lockedTooltip')}>
                                <Box>
                                  <FiInfo color="gray.400" />
                                </Box>
                              </Tooltip>
                            </HStack>
                            <Text fontSize="xl" fontWeight="bold" color="orange.500">
                              {formatDOR(mockDORData.locked)}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {t('estimatedValue')}: {formatUSD(mockDORData.lockedValue)}
                            </Text>
                          </VStack>
                        </CardBody>
                      </Card>
                    </SimpleGrid>

                    {/* 锁定赎回进度 */}
                    <Card bg={cardBg} border="1px" borderColor={borderColor} w="full">
                      <CardBody>
                        <VStack spacing={3}>
                          <HStack justify="space-between" w="full">
                            <Text fontSize="sm">{t('unlockProgress')}</Text>
                            <Text fontSize="sm" fontWeight="bold">
                              {t('unlockProgressDesc')} {mockDORData.unlockProgress}%
                            </Text>
                          </HStack>
                          <Progress 
                            value={mockDORData.unlockProgress} 
                            colorScheme="green" 
                            size="lg"
                            borderRadius="full"
                          />
                          {mockDORData.locked > 0 && (
                            <Button
                              size="sm"
                              variant="link"
                              colorScheme="blue"
                              leftIcon={<FiUsers />}
                              onClick={() => navigate('/invite')}
                            >
                              {t('inviteMoreUsers')}
                            </Button>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  </VStack>

                  {/* 饼图 */}
                  <Card bg={cardBg} border="1px" borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">{t('dorDistribution')}</Heading>
                    </CardHeader>
                    <CardBody>
                      <Box h="200px">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieChartData}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                            >
                              {pieChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <VStack spacing={2} mt={4}>
                        <HStack justify="space-between" w="full">
                          <HStack>
                            <Box w={3} h={3} bg="green.500" borderRadius="full" />
                            <Text fontSize="sm">{t('circulatingPercentage')}</Text>
                          </HStack>
                          <Text fontSize="sm" fontWeight="bold">
                            {((mockDORData.circulating / mockDORData.total) * 100).toFixed(1)}%
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="full">
                          <HStack>
                            <Box w={3} h={3} bg="orange.500" borderRadius="full" />
                            <Text fontSize="sm">{t('lockedPercentage')}</Text>
                          </HStack>
                          <Text fontSize="sm" fontWeight="bold">
                            {((mockDORData.locked / mockDORData.total) * 100).toFixed(1)}%
                          </Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </Grid>
              </VStack>
            </TabPanel>

            {/* 操作 DOR 模块 */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
                  {/* 转换操作 */}
                  <Card bg={cardBg} border="1px" borderColor={borderColor}>
                    <CardHeader>
                      <Heading size="md">{t('convertToLocked')}</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={6}>
                        <Alert status="info" borderRadius="md">
                          <AlertIcon />
                          <Box>
                            <AlertTitle>{t('convertToLocked')}</AlertTitle>
                            <AlertDescription>
                              {t('convertDescription')}
                            </AlertDescription>
                          </Box>
                        </Alert>

                        <FormControl>
                          <FormLabel>{t('convertAmount')}</FormLabel>
                          <InputGroup>
                            <NumberInput
                              value={conversionAmount}
                              onChange={handleAmountChange}
                              min={mockDORData.minConversion}
                              max={mockDORData.circulating}
                              precision={2}
                            >
                              <NumberInputField placeholder={t('enterConvertAmount')} />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                            <InputRightElement width="4.5rem">
                              <Text color="gray.500" fontSize="sm">DOR</Text>
                            </InputRightElement>
                          </InputGroup>
                          <FormHelperText>
                            {t('minMaxInfo', { min: mockDORData.minConversion, max: formatDOR(mockDORData.circulating) })}
                          </FormHelperText>
                          <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<FiMaximize2 />}
                            onClick={setMaxAmount}
                            mt={2}
                          >
                            {t('max')}
                          </Button>
                        </FormControl>

                        {previewData && (
                          <Card variant="outline" w="full">
                            <CardBody>
                              <VStack spacing={3}>
                                <HStack justify="space-between" w="full">
                                  <Text>{t('conversionRate')}</Text>
                                  <Text fontWeight="bold">100%</Text>
                                </HStack>
                                <Divider />
                                <HStack justify="space-between" w="full">
                                  <Text fontSize="lg" fontWeight="bold">{t('expectedGain')}</Text>
                                  <Text fontSize="lg" fontWeight="bold" color="orange.500">
                                    {formatDOR(previewData.convertedAmount)} {t('lockedDORUnit')}
                                  </Text>
                                </HStack>
                              </VStack>
                            </CardBody>
                          </Card>
                        )}

                        <Button
                          colorScheme="blue"
                          size="lg"
                          onClick={onOpen}
                          leftIcon={<FiLock />}
                          rightIcon={<FiArrowRight />}
                          isDisabled={!conversionAmount || parseFloat(conversionAmount) === 0}
                          w="full"
                        >
                          {t('confirmConvert')}
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* 其他操作 */}
                  <VStack spacing={4}>
                    {/* 卖出选项 */}
                    <Card bg={cardBg} border="1px" borderColor={borderColor} w="full">
                      <CardHeader>
                        <Heading size="md">{t('sellCirculating')}</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4}>
                          <Alert status="warning" borderRadius="md">
                            <AlertIcon />
                            <Box>
                              <AlertTitle>{t('priceDeviation')}</AlertTitle>
                              <AlertDescription>
                                {t('priceDeviationDesc')}
                              </AlertDescription>
                            </Box>
                          </Alert>
                          
                          <VStack spacing={2} w="full">
                            <Text fontSize="sm">{t('expectedUSDT')}</Text>
                            <Text fontSize="lg" fontWeight="bold" color="green.500">
                              {formatUSD(mockDORData.circulatingValue)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {t('basedOnPrice')} 0.99 USDT
                            </Text>
                          </VStack>

                          <Button
                            colorScheme="green"
                            variant="outline"
                            leftIcon={<FiExternalLink />}
                            onClick={handleExternalSell}
                            w="full"
                          >
                            {t('externalSell')}
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>

                    {/* 销毁监控 */}
                    <Card bg={cardBg} border="1px" borderColor={borderColor} w="full">
                      <CardHeader>
                        <Heading size="md">{t('destroyMonitor')}</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4}>
                          <Text fontSize="sm">
                            {t('destroyMonitorDesc', { amount: formatDOR(mockDORData.locked) })}
                          </Text>
                          
                          <Button
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<FiUsers />}
                            onClick={() => navigate('/invite')}
                            w="full"
                          >
                            {t('viewInviteChain')}
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>

                    {/* 高级选项 */}
                    <Accordion allowToggle w="full">
                      <AccordionItem>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <Text fontWeight="bold">{t('advancedOptions')}</Text>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                          <VStack spacing={3}>
                            <Text fontSize="sm" color="gray.500">
                              {t('directRedeem')}
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="orange"
                              isDisabled
                            >
                              {t('comingSoon')}
                            </Button>
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </VStack>
                </Grid>
              </VStack>
            </TabPanel>

            {/* 交易历史模块 */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* 统计概览卡片 */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                  <Card bg={cardBg} border="1px" borderColor={borderColor}>
                    <CardBody>
                      <Stat>
                        <StatLabel color="gray.500" fontSize="sm">{t('totalConverted')}</StatLabel>
                        <StatNumber color="blue.500" fontSize="xl">
                          {formatDOR(statsData.totalConverted)}
                        </StatNumber>
                        <StatHelpText fontSize="xs">
                          <StatArrow type="increase" />
                          {t('lockedStatus')}
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} border="1px" borderColor={borderColor}>
                    <CardBody>
                      <Stat>
                        <StatLabel color="gray.500" fontSize="sm">{t('totalDestroyed')}</StatLabel>
                        <StatNumber color="green.500" fontSize="xl">
                          {formatDOR(statsData.totalDestroyed)}
                        </StatNumber>
                        <StatHelpText fontSize="xs">
                          <StatArrow type="increase" />
                          {t('returnedUSDT', { amount: formatUSD(statsData.totalDestroyed) })}
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} border="1px" borderColor={borderColor}>
                    <CardBody>
                      <Stat>
                        <StatLabel color="gray.500" fontSize="sm">{t('totalSold')}</StatLabel>
                        <StatNumber color="purple.500" fontSize="xl">
                          {formatDOR(statsData.totalSold)}
                        </StatNumber>
                        <StatHelpText fontSize="xs">
                          <StatArrow type="decrease" />
                          {t('externalTrading')}
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} border="1px" borderColor={borderColor}>
                    <CardBody>
                      <Stat>
                        <StatLabel color="gray.500" fontSize="sm">{t('totalRecharged')}</StatLabel>
                        <StatNumber color="orange.500" fontSize="xl">
                          {formatDOR(statsData.totalRecharged)}
                        </StatNumber>
                        <StatHelpText fontSize="xs">
                          <StatArrow type="increase" />
                          {t('initialGain')}
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {/* 历史趋势图表 */}
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">{t('dorChangeTrend')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <Box h="250px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={historyChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            stroke={useColorModeValue('gray.600', 'gray.300')}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            stroke={useColorModeValue('gray.600', 'gray.300')}
                          />
                          <RechartsTooltip 
                            contentStyle={{
                              backgroundColor: cardBg,
                              border: `1px solid ${borderColor}`,
                              borderRadius: '8px',
                            }}
                          />
                          <Legend />
                          <Bar dataKey="circulating" fill="#38A169" name={t('circulatingDOR')} />
                          <Bar dataKey="locked" fill="#ED8936" name={t('lockedDOR')} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>

                {/* 交易历史主卡片 */}
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardHeader>
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <Heading size="md">{t('transactionHistory')}</Heading>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<FiDownload />}
                            onClick={exportHistory}
                          >
                            {t('exportCSV')}
                          </Button>
                        </HStack>
                      </HStack>

                      {/* 搜索和筛选工具栏 */}
                      <VStack spacing={3} align="stretch">
                        <HStack spacing={3}>
                          {/* 搜索框 */}
                          <InputGroup size="sm" flex={1}>
                            <InputLeftElement>
                              <FiSearch color="gray.400" />
                            </InputLeftElement>
                            <Input
                              placeholder={t('searchPlaceholder')}
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </InputGroup>

                          {/* 类型筛选 */}
                          <Select
                            size="sm"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            w="140px"
                          >
                            <option value="all">{t('allTypes')}</option>
                            <option value="convert">{t('convert')}</option>
                            <option value="destroy">{t('destroy')}</option>
                            <option value="sell">{t('sell')}</option>
                            <option value="recharge">{t('recharge')}</option>
                          </Select>

                          {/* 排序 */}
                          <Select
                            size="sm"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            w="120px"
                          >
                            <option value="timestamp">{t('time')}</option>
                            <option value="amount">{t('amount')}</option>
                            <option value="usdtImpact">{t('usdtImpact')}</option>
                          </Select>

                          <IconButton
                            size="sm"
                            variant="outline"
                            icon={sortOrder === 'desc' ? <FiChevronDown /> : <FiChevronUp />}
                            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                            aria-label={t('toggleSort')}
                          />
                        </HStack>

                        {/* 结果统计 */}
                        <HStack justify="space-between" fontSize="sm" color="gray.500">
                          <Text>
                            {t('showingRecords', { current: paginatedHistory.length, total: filteredHistory.length })}
                          </Text>
                          <Text>
                            {t('pageInfo', { current: currentPage, total: totalPages })}
                          </Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </CardHeader>

                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      {/* 交易列表 */}
                      {paginatedHistory.length > 0 ? (
                        <VStack spacing={3} align="stretch">
                          {paginatedHistory.map((item) => (
                            <TransactionCard key={item.id} item={item} />
                          ))}
                        </VStack>
                      ) : (
                        <Box textAlign="center" py={8}>
                          <Box mb={4}><FiAlertCircle size={48} color="var(--chakra-colors-gray-400)" /></Box>
                          <Text color="gray.500">{t('noTransactions')}</Text>
                        </Box>
                      )}

                      {/* 分页控件 */}
                      {totalPages > 1 && (
                        <HStack justify="center" spacing={2} pt={4}>
                          <IconButton
                            size="sm"
                            variant="outline"
                            icon={<FiChevronLeft />}
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            isDisabled={currentPage === 1}
                            aria-label={t('previousPage')}
                          />
                          
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                            return (
                              <Button
                                key={pageNum}
                                size="sm"
                                variant={currentPage === pageNum ? "solid" : "outline"}
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <IconButton
                            size="sm"
                            variant="outline"
                            icon={<FiChevronRight />}
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            isDisabled={currentPage === totalPages}
                            aria-label={t('nextPage')}
                          />
                        </HStack>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* 底部行动区 */}
        <HStack justify="center" spacing={4}>
          <Button
            leftIcon={<FiExternalLink />}
            colorScheme="green"
            onClick={handleExternalSell}
          >
            {t('externalSellAction')}
          </Button>
        </HStack>

        {/* 风险免责横幅 */}
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>{t('riskDisclaimer')}</AlertTitle>
            <AlertDescription>
              {t('riskDescription')}
            </AlertDescription>
          </Box>
        </Alert>

        {/* 转换确认模态 */}
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('confirmConversion')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text>
                {t('confirmConversionMessage', { amount: conversionAmount })}
              </Text>
              
              {previewData && (
                <Card variant="outline" w="full">
                  <CardBody>
                    <VStack spacing={3}>
                      <HStack justify="space-between" w="full">
                        <Text>{t('conversionRate')}</Text>
                        <Text fontWeight="bold">100%</Text>
                      </HStack>
                      <HStack justify="space-between" w="full">
                        <Text>{t('gasEstimate')}</Text>
                        <Text color="gray.500">
                          ~{formatUSD(previewData.gasEstimate)}
                        </Text>
                      </HStack>
                      <Divider />
                      <HStack justify="space-between" w="full">
                        <Text fontSize="lg" fontWeight="bold">{t('expectedGain')}</Text>
                        <Text fontSize="lg" fontWeight="bold" color="orange.500">
                          {formatDOR(previewData.convertedAmount)} {t('lockedDORUnit')}
                        </Text>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              )}

              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertTitle>{t('importantReminder')}</AlertTitle>
                  <AlertDescription>
                    {t('importantReminderDesc')}
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConversion}
              isLoading={isLoading}
              loadingText={t('converting')}
              leftIcon={<FiLock />}
            >
              {t('confirmConvert')}
            </Button>
          </ModalFooter>
        </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

// 交易卡片组件
interface TransactionCardProps {
  item: {
    id: number;
    type: string;
    amount: number;
    usdtImpact: number;
    timestamp: string;
    txHash: string;
    status: string;
    description: string;
    gasUsed?: number;
    blockNumber?: number;
    confirmations?: number;
  };
}

const TransactionCard: React.FC<TransactionCardProps> = ({ item }) => {
  const { isOpen, onToggle } = useCollapse();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  const getTypeIcon = () => {
    switch (item.type) {
      case 'convert':
        return { icon: FiLock, color: 'blue.500', label: t('transactionTypes.convert') };
      case 'destroy':
        return { icon: FiUnlock, color: 'green.500', label: t('transactionTypes.destroy') };
      case 'sell':
        return { icon: FiExternalLink, color: 'purple.500', label: t('transactionTypes.sell') };
      case 'recharge':
        return { icon: FiDollar, color: 'orange.500', label: t('transactionTypes.recharge') };
      default:
        return { icon: FiHash, color: 'gray.500', label: item.type };
    }
  };

  const typeInfo = getTypeIcon();

  return (
    <Card 
      bg={cardBg} 
      border="1px" 
      borderColor={borderColor}
      _hover={{ bg: hoverBg }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={onToggle}
    >
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          {/* 主要信息行 */}
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Box
                p={2}
                borderRadius="full"
                bg={`${typeInfo.color}20`}
                color={typeInfo.color}
              >
                <typeInfo.icon size={16} />
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold" fontSize="sm">
                  {typeInfo.label}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {item.description}
                </Text>
              </VStack>
            </HStack>

            <VStack align="end" spacing={0}>
              <Text 
                fontWeight="bold" 
                fontSize="lg"
                color={item.amount > 0 ? 'green.500' : 'red.500'}
              >
                {item.amount > 0 ? '+' : ''}{formatDOR(item.amount)}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {item.timestamp}
              </Text>
            </VStack>
          </HStack>

          {/* 状态和操作 */}
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Badge 
                colorScheme={item.status === 'completed' ? 'green' : 'yellow'}
                size="sm"
                variant="subtle"
              >
                {item.status === 'completed' ? t('status.completed') : t('status.pending')}
              </Badge>
              
              {item.usdtImpact !== 0 && (
                <Text fontSize="xs" color={item.usdtImpact > 0 ? 'green.500' : 'red.500'}>
                  {item.usdtImpact > 0 ? '+' : ''}{formatUSD(item.usdtImpact)} USDT
                </Text>
              )}

              {item.confirmations !== undefined && (
                <Text fontSize="xs" color="gray.500">
                  {item.confirmations} 确认
                </Text>
              )}
            </HStack>

            <HStack spacing={1}>
              <IconButton
                size="xs"
                variant="ghost"
                icon={<FiEye />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`https://testnet.bscscan.com/tx/${item.txHash}`, '_blank');
                }}
                aria-label={t('viewTransaction')}
              />
              <Box 
                as={isOpen ? FiChevronUp : FiChevronDown} 
                color="gray.400" 
                boxSize={4}
              />
            </HStack>
          </HStack>

          {/* 详细信息（可展开） */}
          <Collapse in={isOpen}>
            <VStack spacing={3} align="stretch" pt={3} borderTop="1px" borderColor={borderColor}>
              <SimpleGrid columns={2} spacing={4}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.500">交易哈希</Text>
                  <Text fontSize="xs" fontFamily="mono">
                    {item.txHash.slice(0, 20)}...{item.txHash.slice(-8)}
                  </Text>
                </VStack>
                
                {item.blockNumber && (
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.500">区块号</Text>
                    <Text fontSize="xs">{item.blockNumber.toLocaleString()}</Text>
                  </VStack>
                )}
              </SimpleGrid>

              {item.gasUsed && (
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">{t('gasEstimate')}</Text>
                  <Text fontSize="xs">{formatUSD(item.gasUsed)}</Text>
                </HStack>
              )}
            </VStack>
          </Collapse>
        </VStack>
      </CardBody>
    </Card>
  );
  };

  return renderContent();
};

export default DORManagement; 